import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Link } from '@uhg-abyss/web/ui/Link';
import { config } from '@uhg-abyss/web/tools/config';
import { event } from '@uhg-abyss/web/tools/event';
import { apiGroups } from './apiList';

const FullNameText = styled('span', {
  color: 'rgb(0, 38, 119)',
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: 700,
});
const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});
const StyledLink = styled(Link, {});
const Title = styled('h2', {
  color: 'rgb(0, 38, 119)',
  textAlign: 'left',
  fontSize: '25.63px !important',
  fontWeight: '700',
  minHeight: '32px',
  lineHeight: '32px',
  marginBottom: '5px',
});
const SecContainer = styled('div', {
  textAlign: 'left',
  paddingLeft: '10px',
  marginTop: '30px',
  marginBottom: '30px',
});

const Container = styled('div', {
  display: 'grid',
  textAlign: 'left',
  padding: '0 16px',
  '@screen >= $xs': {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },
  '@screen >= $xl': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const buildEnvURL = (path) => {
  if (path.startsWith('https://') || path.startsWith('http://')) {
    return path;
  }
  const appEnv = config('APP_ENV');
  return appEnv === 'test' || appEnv === 'stage' || appEnv === 'dev'
    ? `/${appEnv}${path}`
    : path;
};

const fireClickEvent = (type) => {
  let fileName;
  let fileURL;
  let fileContext;

  if (type === 'apiOverview') {
    fileName = 'API Overview';
    fileURL = config('API_OVERVIEW');
    fileContext = 'pdf guide';
  } else {
    fileName = 'self-paced user guide';
    fileURL = config('SELF_PACED_USER_GUIDE');
    fileContext = 'self paced chameleon guide';
  }

  event('DOWNLOAD_CLICK', {
    event,
    fileName,
    fileURL,
    fileContext,
  });
};

const fireInteractoinEvent = (type) => {
  event('INTERACTION_EVENT', {
    event,
    interactionName: 'Recommended APIs',
    interactionValue:
      type === 'documentation'
        ? 'Read technical documentation'
        : 'technical demo videos',
    interactionContext: 'page cta link clicks',
  });
};

export const ThirdPart = () => {
  const form = useForm();
  const selectedAPIs = form.getValues('orgInfoPartTwo.selectedAPIs') || [];

  // Define Super Connector APIs
  const superConnectorAPIs = [
    'Member Eligibility Check',
    'Claims Precheck Inquiry',
    'Claims Actions',
    'Benefit Check',
    'Claims Inquiry',
    'Prior Auth and Referral Actions',
    'Document Search',
    'Claim Status Inquiry',
  ];

  // Check if any Super Connector API is selected
  const hasSuperConnectorAPI = selectedAPIs.some((api) => {
    return superConnectorAPIs.includes(api);
  });

  // Build the list of categories to show
  const shownCategories = apiGroups
    .filter((group) => {
      // For Super Connector: show if any super connector API is selected
      if (group.category === 'Super Connector') {
        return hasSuperConnectorAPI;
      }
      // For other categories: show if any of their APIs are selected
      return group.apis.some((api) => {
        return selectedAPIs.includes(api);
      });
    })
    .sort((a, b) => {
      return a.category.localeCompare(b.category);
    });

  const allDemoVideosLink = buildEnvURL(
    '/#/knowledge-base/all-apis-video-demos'
  );

  return (
    <Wrapper>
      <Title>
        Based on the information provided, we recommend the following APIs to
        benefit your organization:
      </Title>
      <Container>
        {shownCategories.map((api) => {
          const apiLink = buildEnvURL(api.link);
          return (
            <div key={api.category} style={{ marginBottom: '5px' }}>
              <FullNameText>{api.category}</FullNameText>
              <ul style={{ paddingLeft: '20px', listStyleType: 'disc' }}>
                <li>
                  <StyledLink
                    href={apiLink}
                    onClick={() => {
                      fireInteractoinEvent('documentation');
                    }}
                    openNewWindow
                    hideIcon
                  >
                    Read technical documentation
                  </StyledLink>
                </li>
              </ul>
            </div>
          );
        })}
      </Container>
      <SecContainer>
        <FullNameText>
          Please take a moment to review the&nbsp;
          <StyledLink
            style={{ fontSize: '18px' }}
            href={allDemoVideosLink}
            onClick={() => {
              fireInteractoinEvent('demoVideos');
            }}
            openNewWindow
            hideIcon
          >
            technical demo videos
          </StyledLink>
          &nbsp;and explore the&nbsp;
          <StyledLink
            style={{ fontSize: '18px' }}
            onClick={() => {
              fireClickEvent('selfPacedGuide');
            }}
            href={config('SELF_PACED_USER_GUIDE')}
            openNewWindow
            hideIcon
          >
            self-paced user guide
          </StyledLink>
          &nbsp;and&nbsp;
          <StyledLink
            style={{ fontSize: '18px' }}
            onClick={() => {
              fireClickEvent('apiOverview');
            }}
            href={config('API_OVERVIEW')}
            openNewWindow
            hideIcon
          >
            API Overview.
          </StyledLink>
        </FullNameText>
      </SecContainer>
    </Wrapper>
  );
};
